/**
 * Core DatePicker Component
 *
 * Composition of Popover + Calendar following shadcn/ui pattern.
 * Supports single date, date range, date-of-birth (with dropdowns),
 * time input, presets, min/max bounds, and RBAC gating.
 *
 * @example
 *   // Basic single date
 *   <DatePicker label="Start Date" value={date} onChange={setDate} />
 *
 *   // Range picker
 *   <DatePicker mode="range" range={range} onRangeChange={setRange} />
 *
 *   // Date of birth with year/month dropdowns
 *   <DatePicker label="DOB" value={dob} onChange={setDob} captionLayout="dropdown" fromYear={1920} toYear={2010} />
 *
 *   // With time input
 *   <DatePicker label="Meeting" value={dt} onChange={setDt} includeTime />
 *
 *   // With presets
 *   <DatePicker label="Due Date" value={d} onChange={setD} presets={[
 *     { label: 'Today', value: new Date() },
 *     { label: 'Tomorrow', value: addDays(new Date(), 1) },
 *   ]} />
 */
import React, { useState, useCallback, useMemo } from 'react';
import { format as formatDate, parse, isValid } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { Calendar } from './Calendar';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/useAuth';
import { hasPermission } from '@/utils/permissions';

// ── Types ────────────────────────────────────────────────────────────────────

export interface DatePreset {
  label: string;
  value: Date;
}

export interface DatePickerProps {
  // ── Mode ──
  /** Selection mode: single date or date range */
  mode?: 'single' | 'range';

  // ── Single mode ──
  /** Current selected date (single mode) */
  value?: Date | string;
  /** Change handler (single mode) — receives Date or undefined */
  onChange?: (date: Date | undefined) => void;

  // ── Range mode ──
  /** Current range (range mode) */
  range?: DateRange;
  /** Change handler (range mode) */
  onRangeChange?: (range: DateRange | undefined) => void;

  // ── Display ──
  /** Label above the trigger */
  label?: string;
  /** Placeholder when no date selected */
  placeholder?: string;
  /** Date format string (date-fns) */
  dateFormat?: string;
  /** Range format string (date-fns) */
  rangeFormat?: string;

  // ── Calendar config ──
  /** Calendar caption layout — "label" (default buttons) or "dropdown" (for DOB) */
  captionLayout?: 'label' | 'dropdown' | 'dropdown-months' | 'dropdown-years';
  /** Start year for dropdown layout */
  fromYear?: number;
  /** End year for dropdown layout */
  toYear?: number;
  /** Number of months visible */
  numberOfMonths?: number;
  /** Minimum selectable date */
  minDate?: Date;
  /** Maximum selectable date */
  maxDate?: Date;
  /** Dates to disable */
  disabledDates?: Date[];
  /** Show days from adjacent months */
  showOutsideDays?: boolean;

  // ── Time ──
  /** Include time input below calendar */
  includeTime?: boolean;

  // ── Presets ──
  /** Quick-select date presets shown as sidebar chips */
  presets?: DatePreset[];

  // ── States ──
  /** Error state */
  error?: boolean;
  /** Helper text / error message */
  helperText?: string;
  /** Required field indicator */
  required?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Full width trigger */
  fullWidth?: boolean;
  /** Trigger width (px or CSS value) */
  width?: string | number;

  // ── RBAC ──
  /** Permission key — hides component if user lacks permission */
  permission?: string;

  /** Custom className for the trigger */
  className?: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function toDate(v: Date | string | undefined): Date | undefined {
  if (!v) return undefined;
  if (v instanceof Date) return isValid(v) ? v : undefined;
  const parsed = new Date(v);
  return isValid(parsed) ? parsed : undefined;
}

function formatSingleDate(date: Date | undefined, fmt: string, includeTime: boolean): string {
  if (!date) return '';
  const base = formatDate(date, fmt);
  if (includeTime) return `${base} ${formatDate(date, 'HH:mm')}`;
  return base;
}

// ── Component ────────────────────────────────────────────────────────────────

export const DatePicker = React.forwardRef<HTMLDivElement, DatePickerProps>(
  (
    {
      mode = 'single',
      value,
      onChange,
      range,
      onRangeChange,
      label,
      placeholder,
      dateFormat = 'PPP',
      rangeFormat = 'PPP',
      captionLayout = 'label',
      fromYear,
      toYear,
      numberOfMonths,
      minDate,
      maxDate,
      disabledDates,
      showOutsideDays = true,
      includeTime = false,
      presets,
      error = false,
      helperText,
      required = false,
      disabled = false,
      fullWidth = false,
      width,
      permission,
      className = '',
    },
    ref,
  ) => {
    const { user } = useAuth();
    if (permission && !hasPermission(user, permission)) return null;

    const [open, setOpen] = useState(false);
    const [timeValue, setTimeValue] = useState('12:00');

    // Normalize value
    const selectedDate = useMemo(() => toDate(value), [value]);

    // Auto-compute numberOfMonths for range mode
    const months = numberOfMonths ?? (mode === 'range' ? 2 : 1);

    // ── Display text ──
    const displayText = useMemo(() => {
      if (mode === 'range') {
        if (!range?.from) return '';
        if (!range.to) return formatDate(range.from, rangeFormat);
        return `${formatDate(range.from, rangeFormat)} – ${formatDate(range.to, rangeFormat)}`;
      }
      return formatSingleDate(selectedDate, dateFormat, includeTime);
    }, [mode, range, selectedDate, dateFormat, rangeFormat, includeTime]);

    // ── Handlers ──
    const handleSingleSelect = useCallback(
      (date: Date | undefined) => {
        if (!date) {
          onChange?.(undefined);
          return;
        }
        if (includeTime) {
          const [hours, minutes] = timeValue.split(':').map(Number);
          date.setHours(hours, minutes);
        }
        onChange?.(date);
        if (!includeTime) setOpen(false);
      },
      [onChange, includeTime, timeValue],
    );

    const handleRangeSelect = useCallback(
      (r: DateRange | undefined) => {
        onRangeChange?.(r);
        if (r?.from && r?.to) setOpen(false);
      },
      [onRangeChange],
    );

    const handleTimeChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newTime = e.target.value;
        setTimeValue(newTime);
        if (selectedDate) {
          const updated = new Date(selectedDate);
          const [hours, minutes] = newTime.split(':').map(Number);
          updated.setHours(hours, minutes);
          onChange?.(updated);
        }
      },
      [selectedDate, onChange],
    );

    const handlePreset = useCallback(
      (preset: DatePreset) => {
        if (mode === 'single') {
          onChange?.(preset.value);
          setOpen(false);
        }
      },
      [mode, onChange],
    );

    // ── Disabled date matcher ──
    const disabledMatcher = useMemo(() => {
      const matchers: any[] = [];
      if (minDate) matchers.push({ before: minDate });
      if (maxDate) matchers.push({ after: maxDate });
      if (disabledDates?.length) matchers.push(...disabledDates);
      return matchers.length > 0 ? matchers : undefined;
    }, [minDate, maxDate, disabledDates]);

    // ── Trigger button ──
    const triggerButton = (
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(!open)}
        className={cn(
          'inline-flex items-center gap-2 rounded-md border px-3 text-left text-sm font-normal transition-colors',
          'bg-white dark:bg-slate-900',
          'border-slate-200 dark:border-slate-700',
          'hover:bg-slate-50 dark:hover:bg-slate-800',
          'focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:ring-offset-1 dark:focus:ring-offset-slate-900',
          'disabled:cursor-not-allowed disabled:opacity-50',
          error && 'border-red-500 dark:border-red-400 focus:ring-red-500',
          fullWidth ? 'w-full' : '',
          'h-9',
          className,
        )}
        style={{ width: !fullWidth && width ? width : undefined, minWidth: fullWidth ? undefined : '220px' }}
      >
        <CalendarIcon className="h-4 w-4 shrink-0 text-slate-400 dark:text-slate-500" />
        {displayText ? (
          <span className="truncate text-slate-900 dark:text-slate-100">{displayText}</span>
        ) : (
          <span className="truncate text-slate-400 dark:text-slate-500">
            {placeholder ?? (mode === 'range' ? 'Select date range' : 'Pick a date')}
          </span>
        )}
      </button>
    );

    // ── Calendar content ──
    const calendarContent = (
      <div className="flex">
        {/* Presets sidebar */}
        {presets && presets.length > 0 && (
          <div className="flex flex-col gap-1 border-r border-slate-200 dark:border-slate-700 p-3 min-w-[130px]">
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wider">
              Presets
            </span>
            {presets.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => handlePreset(preset)}
                className={cn(
                  'text-left text-sm rounded-md px-2.5 py-1.5 transition-colors',
                  'hover:bg-slate-100 dark:hover:bg-slate-800',
                  'text-slate-700 dark:text-slate-300',
                  selectedDate &&
                    preset.value.toDateString() === selectedDate.toDateString() &&
                    'bg-[var(--primary-color)] text-white hover:bg-[var(--primary-color)]',
                )}
              >
                {preset.label}
              </button>
            ))}
          </div>
        )}

        {/* Calendar grid */}
        <div>
          {mode === 'single' ? (
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleSingleSelect}
              numberOfMonths={months}
              captionLayout={captionLayout}
              startMonth={fromYear ? new Date(fromYear, 0) : undefined}
              endMonth={toYear ? new Date(toYear, 11) : undefined}
              disabled={disabledMatcher}
              showOutsideDays={showOutsideDays}
            />
          ) : (
            <Calendar
              mode="range"
              selected={range}
              onSelect={handleRangeSelect}
              numberOfMonths={months}
              disabled={disabledMatcher}
              showOutsideDays={showOutsideDays}
            />
          )}

          {/* Time input */}
          {includeTime && mode === 'single' && (
            <div className="border-t border-slate-200 dark:border-slate-700 px-3 py-2 flex items-center gap-2">
              <label className="text-xs text-slate-500 dark:text-slate-400 font-medium">Time:</label>
              <input
                type="time"
                value={timeValue}
                onChange={handleTimeChange}
                className={cn(
                  'rounded-md border border-slate-200 dark:border-slate-700 px-2 py-1 text-sm',
                  'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100',
                  'focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:ring-offset-1',
                )}
              />
            </div>
          )}
        </div>
      </div>
    );

    return (
      <div ref={ref} className={cn(fullWidth && 'w-full')}>
        {/* Label */}
        {label && (
          <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
        )}

        {/* Popover composition */}
        <div className="relative">
          {triggerButton}

          {open && (
            <>
              {/* Backdrop */}
              <div className="fixed inset-0 z-50" onClick={() => setOpen(false)} />
              {/* Dropdown panel */}
              <div
                className={cn(
                  'absolute left-0 top-full z-50 mt-1',
                  'rounded-lg border border-slate-200 dark:border-slate-700',
                  'bg-white dark:bg-slate-900',
                  'shadow-lg',
                  'overflow-hidden',
                )}
              >
                {calendarContent}
              </div>
            </>
          )}
        </div>

        {/* Helper / error text */}
        {helperText && (
          <p className={cn('mt-1 text-xs', error ? 'text-red-500' : 'text-slate-500 dark:text-slate-400')}>
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

DatePicker.displayName = 'DatePicker';
export default DatePicker;
